/**
 * Arabic for the equipment register — the airport's vehicles and plant, and
 * each stakeholder's. Proposed; pending native aviation review.
 *
 * Two layers, as in `continuity.ts`. The screen's own sentences are literals
 * and the coverage test enforces them. The catalogue below them — the types,
 * the conditions, the sort names, the reasons an item is stopped — is rendered
 * through `t(variable)`, which the extractor cannot see, so it is translated
 * here and pinned by its own test in `src/domain/__tests__/fleet.test.ts`.
 *
 * On the terms: the airside vehicle permit (تصريح دخول الجانب الجوي) and the
 * airside driving permit (رخصة القيادة في الجانب الجوي) are the aerodrome's
 * own, and are deliberately worded apart from the State's vehicle licence
 * (رخصة سير المركبة). Collapsing the three into one word would lose the
 * distinction the whole register is built on.
 */
const ar: Record<string, string> = {
  /* ── the desk ──────────────────────────────────────────────────────── */
  "the equipment register": "سجل المعدات",
  "items on the register": "عناصر في السجل",
  "{airport} the airport's, {other} a stakeholder's": "{airport} للمطار، و{other} لجهة معنية",
  unserviceable: "خارج الخدمة",
  "everything on the register is available": "كل ما في السجل متاح",
  "airside permit expired": "تصريح الجانب الجوي منتهٍ",
  "cannot go airside today: {tags}": "لا يمكنها دخول الجانب الجوي اليوم: {tags}",
  "every airside permit on the register is current": "كل تصاريح الجانب الجوي في السجل سارية",
  "license expired or expiring": "رخصة منتهية أو قاربت الانتهاء",
  "{expired} expired, {soon} within {days} days": "{expired} منتهية، و{soon} خلال {days} يوماً",
  "safety inspection overdue": "فحص السلامة متأخر",
  "no safety inspection is overdue": "لا يوجد فحص سلامة متأخر",
  "no driver assigned": "لا سائق مُسنَد",
  "every item has somebody named against it": "لكل عنصر شخص مُسمّى",

  /* ── the board line ────────────────────────────────────────────────── */
  "{site} holds {n} on the equipment register: {airport} the airport's own and {other} a stakeholder's.":
    "يضم {site} {n} في سجل المعدات: {airport} للمطار نفسه و{other} لجهات معنية.",
  "Stopped today: {permit} with an expired airside permit, {licence} with an expired license, {inspection} with a safety inspection overdue.":
    "موقوفة اليوم: {permit} بتصريح جانب جوي منتهٍ، و{licence} برخصة منتهية، و{inspection} بفحص سلامة متأخر.",
  "Nothing on the register is stopped by an expired permit, license or inspection today.":
    "لا شيء في السجل موقوف اليوم بسبب تصريح أو رخصة أو فحص منتهٍ.",
  "{n} unserviceable: {list}.": "{n} خارج الخدمة: {list}.",
  "{n} carry a date nobody has recorded. A blank in this register reads as 'fine' to every reader who has not checked, which is how a lapsed permit reaches the manoeuvring area.":
    "{n} تحمل تاريخاً لم يسجّله أحد. والفراغ في هذا السجل يُقرأ «سليم» لدى كل من لم يتحقّق، وهكذا يصل تصريح منتهٍ إلى منطقة المناورة.",

  /* ── gaps ──────────────────────────────────────────────────────────── */
  "this register is entered, not read from the platform: this deployment's pack declares no vehicle type, so none of these is a platform asset and none is watched by the rules engine":
    "هذا السجل مُدخَل لا مقروء من المنصة: حزمة هذا التركيب لا تعرّف أي نوع مركبة، فلا شيء منها أصل على المنصة ولا يراقبه محرّك القواعد",
  "the maintenance log shown here is what somebody recorded against the item; work orders raised against the same asset on the platform are not folded into it yet":
    "سجل الصيانة المعروض هنا هو ما سجّله أحد على العنصر؛ أما أوامر العمل الصادرة على الأصل نفسه في المنصة فلم تُدمج فيه بعد",
  "hours run, fuel and utilization are recorded only where somebody typed them, so the maintenance interval cannot be computed from use":
    "ساعات التشغيل والوقود والاستخدام مسجّلة فقط حيث أدخلها أحد، فلا يمكن حساب فترة الصيانة من الاستخدام",
  "nothing is on the register yet": "لا شيء في السجل بعد",

  /* ── answering ─────────────────────────────────────────────────────── */
  "Nothing on the register has a license, permit or insurance expired or expiring within {days} days.":
    "لا يوجد في السجل رخصة أو تصريح أو تأمين منتهٍ أو ينتهي خلال {days} يوماً.",
  "Every safety inspection on the register is scheduled and none is overdue.":
    "كل فحوص السلامة في السجل مجدولة ولا شيء منها متأخر.",
  "Every item on the register has a driver named against it.": "لكل عنصر في السجل سائق مُسمّى.",
  "Nothing on the register is unserviceable.": "لا شيء في السجل خارج الخدمة.",
  "{name}: {n}{down}": "{name}: {n}{down}",
  ", {n} unserviceable": "، {n} خارج الخدمة",
  "{tag} {name}: {last}": "{tag} {name}: {last}",
  "nothing recorded": "لا شيء مسجَّل",
  "{n} item is|{n} items are": "لا عناصر|عنصر واحد|عنصران|{n} عناصر|{n} عنصراً|{n} عنصر",

  /* ── the screen ────────────────────────────────────────────────────── */
  "Every vehicle and item of plant on the airport — the airport's own and each stakeholder's — with its condition, where it is, who drives it, when its license, insurance and airside permit expire, when its safety inspection falls due, and the maintenance done on it. Sort by any of them; what is expired, overdue or unrecorded comes first in every order.":
    "كل مركبة وكل معدة في المطار — ما يملكه المطار وما تملكه كل جهة معنية — بحالتها، وموقعها، ومن يقودها، ومتى تنتهي رخصتها وتأمينها وتصريح الجانب الجوي، ومتى يحين فحص السلامة، والصيانة التي أُجريت عليها. رتّبها بأيٍّ منها؛ وما هو منتهٍ أو متأخر أو غير مسجَّل يأتي أولاً في كل ترتيب.",
  "Entered, not read from the platform: this deployment's installed pack declares no vehicle type, so none of this is a platform asset and none of it is watched by the rules engine. The register persists under the signed-in person and can be edited by whoever holds it.":
    "مُدخَل لا مقروء من المنصة: الحزمة المثبّتة في هذا التركيب لا تعرّف أي نوع مركبة، فلا شيء من هذا أصل على المنصة ولا يراقبه محرّك القواعد. ويُحفظ السجل باسم الشخص المسجَّل دخوله، ويعدّله من يتولّاه.",
  "Read from the platform. Each item is an asset of a type the airports pack declares, and the rules engine watches the same dates — a license, an insurance or an airside permit that lapses raises an alert on the day it lapses, without anybody opening this screen. A date nobody has recorded is reported as unknown, never as met.":
    "مقروء من المنصة. كل عنصر أصل من نوع تعرّفه حزمة المطارات، ومحرّك القواعد يراقب التواريخ نفسها — فرخصة أو تأمين أو تصريح جانب جوي ينتهي يرفع إنذاراً يوم انتهائه، دون أن يفتح أحد هذه الشاشة. والتاريخ الذي لم يسجّله أحد يُبلَّغ عنه بأنه غير معلوم، لا بأنه مستوفى.",
  "Sort by": "الترتيب حسب",
  "Every type": "كل الأنواع",
  Everybody: "الجميع",
  "{shown} of {total} shown": "معروض {shown} من {total}",
  " · entered by {who}": " · أدخلها {who}",
  "Nothing on the register matches this filter.": "لا شيء في السجل يطابق هذا التصفية.",
  Driver: "السائق",
  "License": "الرخصة",
  "Airside permit": "تصريح الجانب الجوي",
  "Safety inspection": "فحص السلامة",
  "What is expiring? What is unserviceable? Whose equipment is on the airport?":
    "ما الذي ينتهي قريباً؟ وما الخارج عن الخدمة؟ ومعدات من في المطار؟",
  "in {n}": "خلال {n}",
  stakeholder: "جهة معنية",
  "permit {date}": "التصريح {date}",
  "no permit date": "لا تاريخ للتصريح",
  "none assigned": "لا أحد مُسنَد",
  "{owner} · {kind}": "{owner} · {kind}",
  "What stops it today": "ما يوقفها اليوم",
  "Nothing recorded against it: every date is present and in date.": "لا شيء مسجَّل عليها: كل التواريخ موجودة وسارية.",
  "{n} ago": "قبل {n}",
  "On record": "في السجل",
  Registration: "رقم اللوحة",
  "Inspection result": "نتيجة الفحص",
  "Maintenance log": "سجل الصيانة",
  "Nothing recorded against it.": "لا شيء مسجَّل عليها.",
  "{n} entry|{n} entries": "لا قيود|قيد واحد|قيدان|{n} قيود|{n} قيداً|{n} قيد",

  /* ── the catalogue the extractor cannot see ────────────────────────── */
  Sweepers: "الكنّاسات",
  "Surface plant": "معدات الأسطح",
  Tankers: "الصهاريج",
  Earthmoving: "معدات الحفر والتسوية",
  "Access platforms": "منصات الوصول",
  Haulage: "النقل والقلّابات",
  "Rescue and fire": "الإنقاذ والإطفاء",
  "Power and lighting": "الطاقة والإضاءة",
  "Ground support": "المناولة الأرضية",
  Vehicles: "المركبات",

  Serviceable: "صالحة للخدمة",
  Degraded: "متدهورة",
  Unserviceable: "خارج الخدمة",
  /* "Not assessed", "expired", "expiring", "overdue" and "scheduled" are
     already defined in ar/engineering.ts and ar/executive.ts, and one key
     lives in one file. */

  "Driver assigned": "السائق المُسنَد",
  "License expiry": "انتهاء الرخصة",

  /* the reasons an item is stopped */
  missing: "غير مسجَّل",
  down: "متوقفة",
  "vehicle licence": "رخصة سير المركبة",
  "airside vehicle permit": "تصريح دخول الجانب الجوي",
  insurance: "التأمين",
  "driver's airside permit": "رخصة القيادة في الجانب الجوي",
  "no vehicle licence expiry recorded": "لا تاريخ مسجَّل لانتهاء رخصة المركبة",
  "no airside permit expiry recorded": "لا تاريخ مسجَّل لانتهاء تصريح الجانب الجوي",
  "no insurance expiry recorded": "لا تاريخ مسجَّل لانتهاء التأمين",
  "no driver permit expiry recorded": "لا تاريخ مسجَّل لانتهاء رخصة السائق",
  "no safety inspection scheduled": "لا فحص سلامة مجدول",
  /* lower case: the reason an item is stopped, not the column heading */
  "safety inspection": "فحص السلامة",

  /* maintenance log kinds */
  repair: "إصلاح",
  defect: "عطل",
  /* ── off the road, departments, and the history page ───────────────── */
  "off the road": "خارج الخدمة",
  "{tags} — {parts} of them waiting for parts, which is a supplier's queue, not a crew's":
    "{tags} — منها {parts} بانتظار قطع غيار، وهذا طابور مورّد لا طابور فريق عمل",
  "{n} off the road: {list}.": "{n} خارج الخدمة: {list}.",
  ", {n} off the road": "، {n} خارج الخدمة",
  "Awaiting parts": "بانتظار قطع الغيار",
  "awaiting parts": "بانتظار قطع الغيار",

  Department: "الإدارة",
  "Every department": "كل الإدارات",
  "used by {dept}": "تستخدمها {dept}",
  "Department: {dept}": "الإدارة: {dept}",
  "No department recorded": "لا إدارة مسجَّلة",

  "History and notes": "السجل والملاحظات",
  /* "severity {n}", "Kind", "Note", "Saving…" and "Creating…" already live in
     ar/decisions.ts, ar/maintenance.ts, ar/boards.ts, ar/platform.ts and
     ar/sky.ts. One key, one file. */
  "Driver assigned: {who}": "السائق المُسنَد: {who}",
  "Work order {number} raised against {tag}.": "صدر أمر العمل {number} على {tag}.",
  "Open the work-orders board": "افتح لوحة أوامر العمل",
  "The work order was not raised: {why}": "لم يصدر أمر العمل: {why}",

  "{site} · {suite} · {screen}": "{site} · {suite} · {screen}",
  "Back to the register": "العودة إلى السجل",
  "Nothing on the register is tagged {tag}.": "لا يوجد في السجل ما يحمل الرمز {tag}.",
  "No work order has been raised against {tag}.": "لم يصدر أي أمر عمل على {tag}.",
  "The platform returned the first page of the site's work orders and said it was truncated, so older work against this item may not be in the list above.":
    "أعادت المنصة الصفحة الأولى من أوامر عمل الموقع وذكرت أنها مقتطعة، فقد لا تظهر في القائمة أعلاه أعمال أقدم على هذا العنصر.",
  "No open defect against {tag}.": "لا يوجد عطل مفتوح على {tag}.",
  "What happened, what you did, what the next person needs to know":
    "ما الذي حدث، وما الذي فعلته، وما يحتاج الشخص التالي معرفته",
  "Add the note": "أضف الملاحظة",
  "Nothing has been written about {tag} yet.": "لم يُكتب شيء عن {tag} بعد.",
  "Nothing recorded. On a platform-read register the log lives in the work orders above; the entered register carries its own.":
    "لا شيء مسجَّل. في السجل المقروء من المنصة يوجد السجل في أوامر العمل أعلاه؛ أما السجل المُدخَل فيحمل سجله الخاص.",
  ", last {date}": "، آخرها {date}",
  "{n} entry on the register|{n} entries on the register":
    "لا قيود في السجل|قيد واحد في السجل|قيدان في السجل|{n} قيود في السجل|{n} قيداً في السجل|{n} قيد في السجل",
};
export default ar;
